import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { t } from "@/i18n";
import { toast } from "sonner";
import {
  ArrowLeft, Mail, Phone, Crown, History, AlertTriangle, MapPin,
  Sparkles, ChevronRight, Check, Heart, Briefcase, PartyPopper, Users as UsersIcon, Utensils,
  StickyNote, UserCog, Globe, Footprints, PhoneCall, UserPlus,
} from "lucide-react";

const FLOW = [
  { key: "confirmed",      labelKey: "reservations.statuses.confirmed" },
  { key: "arrived",        labelKey: "reservations.statuses.arrived" },
  { key: "waiting_lounge", labelKey: "reservations.statuses.waiting_lounge" },
  { key: "table_ready",    labelKey: "reservations.statuses.table_ready" },
  { key: "seated",         labelKey: "reservations.statuses.seated" },
  { key: "completed",      labelKey: "reservations.statuses.completed" },
];
const STATUS_RANK = Object.fromEntries(FLOW.map((f, i) => [f.key, i]));

const OCCASION_ICON = {
  dinner: Utensils, romantic: Heart, birthday: PartyPopper,
  business: Briefcase, family: UsersIcon, event: Sparkles,
};

export const SOURCE_ICON = {
  online: Globe,
  manual: UserPlus,
  phone: PhoneCall,
  walk_in: Footprints,
  vip: Crown,
};

function relativeTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const diff = Date.now() - d.getTime();
  const m = Math.round(diff / 60000);
  if (m < 1) return "gerade eben";
  if (m < 60) return `vor ${m} Min.`;
  const h = Math.floor(m / 60);
  if (h < 24) return `vor ${h} Std.`;
  const days = Math.floor(h / 24);
  return `vor ${days} Tg.`;
}

export default function ReservationDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [r, setR] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [history, setHistory] = useState(null);
  const [tables, setTables] = useState([]);
  const [staff, setStaff] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [busy, setBusy] = useState(false);
  const [editingNotes, setEditingNotes] = useState(false);
  const [internalNotes, setInternalNotes] = useState("");

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/reservations/${id}`);
      setR(data);
      setInternalNotes(data.internal_notes || "");
      api.get(`/guests/history`, { params: { email: data.guest_email } })
        .then((res) => setHistory(res.data)).catch(() => {});
      api.get("/tables").then((res) => setTables(res.data)).catch(() => {});
      api.get("/staff").then((res) => setStaff(res.data)).catch(() => {});
      api.get("/rooms").then((res) => setRooms(res.data)).catch(() => {});
    } catch {
      setNotFound(true);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const update = async (patch, successMsg) => {
    setBusy(true);
    try {
      const { data } = await api.patch(`/reservations/${id}`, patch);
      setR(data);
      if (successMsg) toast.success(successMsg);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Fehler");
    } finally {
      setBusy(false);
    }
  };

  const saveNotes = () => {
    update({ internal_notes: internalNotes }, "Notiz gespeichert");
    setEditingNotes(false);
  };

  const advanceTo = (status) => update({ status }, t(`reservations.statuses.${status}`));
  const assignTable = (table_id) => update({ table_id }, "Tisch zugewiesen");
  const assignWaiter = (assigned_waiter_id) => update({ assigned_waiter_id }, "Kellner zugewiesen");

  if (notFound) {
    return (
      <div className="px-6 lg:px-10 py-16 max-w-4xl mx-auto text-center">
        <h1 className="font-heading text-3xl font-light mb-4">{t("reservations.detail.not_found")}</h1>
        <button onClick={() => navigate("/dashboard/reservations")} className="btn-primary text-xs inline-flex items-center gap-2" data-testid="detail-back-empty">
          <ArrowLeft className="w-3.5 h-3.5" /> {t("reservations.detail.back")}
        </button>
      </div>
    );
  }
  if (!r) {
    return <div className="px-6 lg:px-10 py-16 text-sm text-[var(--text-muted)]" data-testid="detail-loading">{t("common.loading")}</div>;
  }

  const currentRank = STATUS_RANK[r.status] ?? -1;
  const room = r.preferred_room_id ? rooms.find((x) => x.room_id === r.preferred_room_id) : null;
  const assignedTable = r.table_id ? tables.find((x) => x.table_id === r.table_id) : null;
  const assignedWaiter = r.assigned_waiter_id ? staff.find((x) => x.user_id === r.assigned_waiter_id) : null;
  const freeTables = tables.filter((tbl) => tbl.status === "free" || tbl.table_id === r.table_id);
  const waiters = staff.filter((s) => ["waiter", "host"].includes(s.role));
  const OccasionIcon = OCCASION_ICON[r.reservation_type] || Utensils;
  const SourceIcon = SOURCE_ICON[r.source] || Globe;

  const tier = history?.is_vip ? { label: "VIP", color: "bg-gold/20 text-gold border-gold/40", icon: Crown }
              : history?.is_regular ? { label: "Stammgast", color: "bg-emerald-500/10 text-emerald-300 border-emerald-500/30", icon: Heart }
              : null;

  return (
    <div className="animate-fade-up" data-testid="reservation-detail-page" style={{ animationDuration: "260ms" }}>
      {/* ===== STICKY HEADER ===== */}
      <div className="sticky top-0 lg:top-0 z-30 bg-gradient-to-b from-[#0c0c0c] to-[#0A0A0A]/95 backdrop-blur-sm border-b border-white/[0.04]">
        <div className="max-w-5xl mx-auto px-6 lg:px-10 py-5 flex items-center justify-between gap-4">
          <button
            onClick={() => navigate("/dashboard/reservations")}
            data-testid="detail-back"
            className="text-[var(--text-muted)] hover:text-gold transition-colors inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em]"
          >
            <ArrowLeft className="w-3.5 h-3.5" /> {t("reservations.detail.back")}
          </button>
          <div className="text-right text-[10px] text-[var(--text-dim)] tracking-[0.25em] uppercase">
            #{r.reservation_id?.slice(-6)}
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-6 lg:px-10 py-10 space-y-10">
        {/* ===== HERO ===== */}
        <div className="flex items-start gap-5">
          <div className="w-16 h-16 rounded-full bg-gradient-to-br from-gold/30 to-gold/5 border border-gold/30 flex items-center justify-center font-heading text-3xl text-gold flex-shrink-0">
            {r.guest_name?.[0]?.toUpperCase()}
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-3 flex-wrap mb-2">
              <h1 className="font-heading text-3xl sm:text-4xl font-light tracking-tight truncate" data-testid="detail-guest-name">{r.guest_name}</h1>
              {tier && (
                <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-[10px] border ${tier.color}`} data-testid="detail-vip">
                  <tier.icon className="w-3 h-3" /> {tier.label}
                </span>
              )}
              <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-[10px] border bg-white/[0.03] text-[var(--text-muted)] border-white/10" data-testid={`detail-source-${r.source}`}>
                <SourceIcon className="w-3 h-3 text-gold" /> {t(`reservations.sources.${r.source}`) || r.source}
              </span>
            </div>
            <div className="flex flex-wrap items-center gap-x-5 gap-y-1 text-sm text-[var(--text-muted)]">
              <span className="font-heading text-gold text-xl">{r.time}</span>
              <span>·</span>
              <span>{r.date}</span>
              <span>·</span>
              <span>{r.party_size} {t("common.guests")}</span>
              <span>·</span>
              <span className="inline-flex items-center gap-1.5">
                <OccasionIcon className="w-3.5 h-3.5 text-gold" />
                {t(`reservations.types.${r.reservation_type}`) || r.reservation_type}
              </span>
            </div>
          </div>
        </div>

        {/* ===== TIMELINE ===== */}
        <section className="surface-1 rounded-md p-6">
          <FlowTimeline reservation={r} currentRank={currentRank} />
        </section>

        {/* ===== ACTIONS ===== */}
        <Section title={t("reservations.detail.next_action")}>
          <div className="flex flex-wrap gap-2">
            {r.status === "pending" && (
              <ActionBtn testid="action-confirm" onClick={() => advanceTo("confirmed")} disabled={busy} variant="primary" icon={Check}>{t("reservations.actions.confirm")}</ActionBtn>
            )}
            {["confirmed"].includes(r.status) && (
              <ActionBtn testid="action-arrived" onClick={() => advanceTo("arrived")} disabled={busy} variant="primary">{t("reservations.actions.arrive")}</ActionBtn>
            )}
            {["confirmed", "arrived"].includes(r.status) && (
              <ActionBtn testid="action-lounge" onClick={() => advanceTo("waiting_lounge")} disabled={busy}>{t("reservations.actions.to_lounge")}</ActionBtn>
            )}
            {["arrived", "waiting_lounge", "table_ready"].includes(r.status) && (
              <ActionBtn testid="action-seat" onClick={() => advanceTo("seated")} disabled={busy} variant="primary">{t("reservations.actions.seat")}</ActionBtn>
            )}
            {r.status === "seated" && (
              <ActionBtn testid="action-complete" onClick={() => advanceTo("completed")} disabled={busy} variant="primary">{t("reservations.actions.complete")}</ActionBtn>
            )}
            {!["completed", "cancelled", "no_show"].includes(r.status) && (
              <>
                <ActionBtn testid="action-cancel" onClick={() => advanceTo("cancelled")} disabled={busy} variant="danger">{t("reservations.actions.cancel")}</ActionBtn>
                <ActionBtn testid="action-noshow" onClick={() => advanceTo("no_show")} disabled={busy} variant="ghost">{t("reservations.actions.no_show")}</ActionBtn>
              </>
            )}
          </div>
        </Section>

        <div className="grid lg:grid-cols-2 gap-6">
          {/* ===== GUEST ===== */}
          <Section title={t("reservations.detail.guest")} icon={UsersIcon}>
            <div className="space-y-3 surface-1 rounded-md p-5">
              {r.guest_email && <Row icon={Mail} label="E-Mail" value={<a href={`mailto:${r.guest_email}`} className="hover:text-gold transition-colors">{r.guest_email}</a>} />}
              {r.guest_phone && <Row icon={Phone} label="Telefon" value={<a href={`tel:${r.guest_phone}`} className="hover:text-gold transition-colors">{r.guest_phone}</a>} />}
              {history && (
                <div className="grid grid-cols-3 gap-2 mt-4 pt-4 border-t border-white/5">
                  <Stat label="Besuche" value={history.total_reservations} testid="guest-total-visits" />
                  <Stat label="Abgeschl." value={history.completed} accent="gold" testid="guest-completed" />
                  <Stat label="No-Shows" value={history.no_shows} accent={history.no_shows > 0 ? "danger" : ""} testid="guest-noshows" />
                </div>
              )}
              {history?.favourite_occasion && (
                <div className="text-xs text-[var(--text-muted)] flex items-center gap-2">
                  <Sparkles className="w-3 h-3 text-gold" />
                  Bevorzugt: <span className="text-[var(--text-primary)]">{t(`reservations.types.${history.favourite_occasion}`)}</span>
                </div>
              )}
              {history?.last_visit_date && (
                <div className="text-xs text-[var(--text-muted)] flex items-center gap-2">
                  <History className="w-3 h-3 text-gold" />
                  Letzter Besuch: <span className="text-[var(--text-primary)]">{history.last_visit_date}</span>
                </div>
              )}
              {history?.common_allergies?.length > 0 && (
                <div className="text-xs text-[var(--text-muted)] flex items-start gap-2">
                  <AlertTriangle className="w-3 h-3 text-red-300 mt-0.5 flex-shrink-0" />
                  <div>Bekannte Allergien: {history.common_allergies.map((a, i) => (
                    <span key={i} className="ml-1 px-1.5 py-0.5 rounded-full bg-red-500/10 text-red-300 text-[10px] border border-red-500/20">{a}</span>
                  ))}</div>
                </div>
              )}
            </div>
          </Section>

          {/* ===== OPERATIONAL ===== */}
          <Section title={t("reservations.detail.operational")} icon={Sparkles}>
            <div className="space-y-3 surface-1 rounded-md p-5">
              {(r.allergies || []).length > 0 && (
                <div className="surface-2 rounded-md p-3 border-l-2 border-red-500/40">
                  <div className="text-[10px] text-red-300 uppercase tracking-wider mb-1.5 flex items-center gap-1.5">
                    <AlertTriangle className="w-3 h-3" /> Allergien dieser Reservierung
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {r.allergies.map((a, i) => (
                      <span key={i} className="px-2 py-0.5 rounded-full bg-red-500/10 text-red-300 text-[11px] border border-red-500/20">{a}</span>
                    ))}
                  </div>
                </div>
              )}
              {r.notes && <Row icon={StickyNote} label="Gast-Wunsch" value={<span className="italic text-[var(--text-primary)]">„{r.notes}"</span>} />}
              {room && <Row icon={MapPin} label="Bevorzugter Raum" value={room.name} />}

              <div className="pt-2">
                <div className="text-[10px] text-[var(--text-muted)] uppercase tracking-wider mb-2 flex items-center justify-between">
                  <span className="flex items-center gap-1.5"><StickyNote className="w-3 h-3 text-gold" /> Interne Notiz</span>
                  {!editingNotes && (
                    <button onClick={() => setEditingNotes(true)} data-testid="edit-internal-notes" className="text-gold hover:text-gold-light normal-case text-[11px] tracking-normal">Bearbeiten</button>
                  )}
                </div>
                {editingNotes ? (
                  <div>
                    <textarea
                      value={internalNotes}
                      onChange={(e) => setInternalNotes(e.target.value)}
                      rows={3}
                      data-testid="internal-notes-textarea"
                      placeholder="Geschenk übergeben, Lieblingsplatz am Fenster, Geburtstagstorte vorbereiten…"
                      className="w-full bg-[#18140F] border border-white/10 rounded-md p-3 text-sm text-[var(--text-primary)] focus:outline-none focus:border-gold/40 resize-none"
                    />
                    <div className="flex gap-2 mt-2">
                      <button onClick={() => { setEditingNotes(false); setInternalNotes(r.internal_notes || ""); }} className="btn-ghost text-[11px] flex-1">{t("common.cancel")}</button>
                      <button onClick={saveNotes} data-testid="save-internal-notes" className="btn-primary text-[11px] flex-1">{t("common.save")}</button>
                    </div>
                  </div>
                ) : (
                  <div className="text-sm text-[var(--text-primary)] italic min-h-[1.5rem]" data-testid="internal-notes-display">
                    {r.internal_notes || <span className="text-[var(--text-dim)] not-italic">Noch keine interne Notiz</span>}
                  </div>
                )}
              </div>

              <div className="grid sm:grid-cols-2 gap-3 pt-2">
                <Assignment icon={MapPin} label="Zugewiesener Tisch" currentLabel={assignedTable ? `${assignedTable.name} · ${assignedTable.seats} Pl.` : null} testid="assign-table">
                  <select value={r.table_id || ""} onChange={(e) => assignTable(e.target.value || null)} data-testid="assign-table-select"
                          className="w-full bg-[var(--bg-rooftop)] border border-white/10 rounded-md h-9 px-2 text-xs focus:outline-none focus:border-gold/40">
                    <option value="">wählen —</option>
                    {freeTables.map((tbl) => (
                      <option key={tbl.table_id} value={tbl.table_id}>{tbl.name} · {tbl.seats} Pl.</option>
                    ))}
                  </select>
                </Assignment>
                <Assignment icon={UserCog} label="Zugewiesener Kellner" currentLabel={assignedWaiter ? `${assignedWaiter.name} · ${t(`staff.roles.${assignedWaiter.role}`)}` : null} testid="assign-waiter">
                  <select value={r.assigned_waiter_id || ""} onChange={(e) => assignWaiter(e.target.value || null)} data-testid="assign-waiter-select"
                          className="w-full bg-[var(--bg-rooftop)] border border-white/10 rounded-md h-9 px-2 text-xs focus:outline-none focus:border-gold/40">
                    <option value="">wählen —</option>
                    {waiters.map((s) => (
                      <option key={s.user_id} value={s.user_id}>{s.name} ({t(`staff.roles.${s.role}`)})</option>
                    ))}
                  </select>
                </Assignment>
              </div>
            </div>
          </Section>
        </div>

        {/* ===== HISTORY ===== */}
        {history?.last_visits?.length > 1 && (
          <Section title={t("reservations.detail.history")} icon={History}>
            <div className="surface-1 rounded-md p-5 space-y-1.5">
              {history.last_visits.filter((v) => v.reservation_id !== r.reservation_id).slice(0, 6).map((v, i) => (
                <div key={i} data-testid={`guest-history-${i}`} className="flex items-center justify-between py-2 px-3 surface-2 rounded-md text-xs">
                  <div className="flex items-center gap-3">
                    <span className="font-heading text-gold">{v.date}</span>
                    <span className="text-[var(--text-muted)]">{v.time} · {v.party_size} Gäste · {t(`reservations.types.${v.reservation_type}`) || v.reservation_type}</span>
                  </div>
                  <span className="text-[10px] text-[var(--text-dim)]">{t(`reservations.statuses.${v.status}`)}</span>
                </div>
              ))}
            </div>
          </Section>
        )}
      </div>
    </div>
  );
}

/* ---------- subcomponents ---------- */
function FlowTimeline({ reservation, currentRank }) {
  const history = reservation.status_history || [];
  const eventByStatus = {};
  history.forEach((h) => { if (!eventByStatus[h.status]) eventByStatus[h.status] = h.at; });
  const terminal = ["cancelled", "no_show"].includes(reservation.status);

  return (
    <div className="relative" data-testid="reservation-timeline">
      <div className="flex items-start justify-between gap-1 relative">
        {FLOW.map((step, i) => {
          const isPast = i <= currentRank && !terminal;
          const isCurrent = i === currentRank && !terminal;
          const at = eventByStatus[step.key];
          return (
            <React.Fragment key={step.key}>
              <div className="flex flex-col items-center flex-1 min-w-0">
                <div data-testid={`timeline-step-${step.key}`}
                  className={`w-8 h-8 rounded-full flex items-center justify-center text-[11px] font-heading border transition-all duration-300 ${
                    isCurrent ? "bg-gold text-black border-gold pulse-gold"
                    : isPast ? "bg-gold/20 text-gold border-gold/40"
                    : "bg-white/[0.02] text-[var(--text-dim)] border-white/5"
                  }`}>
                  {isPast && !isCurrent ? <Check className="w-3.5 h-3.5" /> : i + 1}
                </div>
                <div className={`text-[10px] mt-2 text-center leading-tight max-w-[90px] ${isPast || isCurrent ? "text-[var(--text-primary)]" : "text-[var(--text-dim)]"}`}>
                  {t(step.labelKey)}
                </div>
                {at && <div className="text-[9px] text-gold/70 mt-1 font-heading">{relativeTime(at)}</div>}
              </div>
              {i < FLOW.length - 1 && (
                <div className={`flex-1 h-px mt-4 -mx-1 ${i < currentRank && !terminal ? "bg-gold/40" : "bg-white/5"}`} />
              )}
            </React.Fragment>
          );
        })}
      </div>
      {terminal && (
        <div className="mt-5 text-center" data-testid="timeline-terminal">
          <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-red-500/10 text-red-300 border border-red-500/20 text-xs">
            {t(`reservations.statuses.${reservation.status}`)}
          </span>
        </div>
      )}
    </div>
  );
}

function Section({ title, icon: Icon, children }) {
  return (
    <section>
      <div className="mb-4 flex items-center gap-2">
        {Icon && <Icon className="w-3.5 h-3.5 text-[var(--gold-warm)]" strokeWidth={1.4} />}
        <h3 className="font-editorial italic text-[1.35rem] leading-none text-[var(--text-primary)]">{title}</h3>
      </div>
      {children}
    </section>
  );
}

function Row({ icon: Icon, label, value }) {
  return (
    <div className="flex items-start gap-3 text-sm">
      {Icon && <Icon className="w-3.5 h-3.5 text-[var(--text-dim)] mt-1 flex-shrink-0" strokeWidth={1.5} />}
      <div className="min-w-0 flex-1">
        <div className="text-[10px] text-[var(--text-dim)] uppercase tracking-wider mb-0.5">{label}</div>
        <div className="text-[var(--text-primary)] break-words">{value}</div>
      </div>
    </div>
  );
}

function Stat({ label, value, accent, testid }) {
  const color = accent === "gold" ? "text-gold" : accent === "danger" ? "text-red-300" : "text-[var(--text-primary)]";
  return (
    <div className="surface-2 rounded-md p-3" data-testid={testid}>
      <div className={`font-heading text-2xl font-light ${color}`}>{value}</div>
      <div className="text-[10px] text-[var(--text-dim)] uppercase tracking-wider mt-1">{label}</div>
    </div>
  );
}

function ActionBtn({ children, onClick, disabled, variant = "default", icon: Icon, testid }) {
  const base = "text-[11px] px-4 py-2 rounded-full inline-flex items-center gap-1.5 transition-all duration-200 disabled:opacity-40 disabled:cursor-not-allowed";
  const variants = {
    primary: "bg-gold text-black hover:bg-gold-light",
    default: "bg-white/5 text-[var(--text-primary)] border border-white/10 hover:bg-white/10",
    ghost:   "text-[var(--text-muted)] hover:text-[var(--text-primary)]",
    danger:  "bg-red-500/10 text-red-300 border border-red-500/20 hover:bg-red-500/20",
  };
  return (
    <button onClick={onClick} disabled={disabled} data-testid={testid} className={`${base} ${variants[variant]}`}>
      {Icon && <Icon className="w-3 h-3" />}
      {children}
      <ChevronRight className="w-3 h-3 opacity-50" />
    </button>
  );
}

function Assignment({ icon: Icon, label, currentLabel, children, testid }) {
  return (
    <div data-testid={testid} className="surface-2 rounded-md p-3">
      <div className="text-[10px] text-[var(--text-dim)] uppercase tracking-wider mb-2 flex items-center gap-1.5">
        <Icon className="w-3 h-3 text-gold" strokeWidth={1.5} /> {label}
      </div>
      <div className="text-xs text-[var(--text-primary)] mb-2 truncate" data-testid={`${testid}-current`}>
        {currentLabel || <span className="text-[var(--text-dim)]">Nicht zugewiesen</span>}
      </div>
      {children}
    </div>
  );
}
